/* Auto Fix Auto Electricians - demo line. TEMPLATE-OWNED; the page copy is readout.js, yours.
 *
 * The transport is the Pipecat JS SDK over Daily WebRTC. The previous version of this
 * file spoke SmallWebRTC signalling by hand to avoid a bundler; what that bought in
 * simplicity it lost in everything the SDK does that a hand-rolled peer connection
 * does not — renegotiation, trickle ICE, reconnection, and a TURN relay for callers
 * whose network will not carry a direct connection.
 *
 * The protocol, in full:
 *   GET  /api/config             -> {passcodeRequired, maxCallSeconds, outbound}
 *   POST /api/session {passcode} -> {token, expiresIn}
 *   POST /api/connect {token}    -> {dailyRoom, dailyToken, sessionId}
 *   client.connect(that)         -- DailyTransport maps the keys itself.
 *
 * `POST /api/dial` is the other direction - the assistant rings the visitor - and it is
 * outbound/index.html's, not this page's. What the two share is call-kit.js.
 *
 * Read call-kit.js's audio note before touching anything that plays sound. Echo
 * cancellation depends on where the bot's voice is routed, and it fails silently.
 */

import { PipecatClient } from "@pipecat-ai/client-js";
import { DailyTransport } from "@pipecat-ai/daily-transport";

import {
  accentColour,
  clock,
  createCaptions,
  createNotes,
  createRinger,
  createToken,
  openAudioContext,
  postJson,
  reportAudioConstraints,
  startWave,
  tap,
} from "./call-kit.js";
import { renderOpeners, renderPitch } from "./readout-kit.js";
import { OPENERS, PITCH } from "./readout.js";

(() => {
  "use strict";

  const $ = (id) => document.getElementById(id);

  const el = {
    screens: {
      gate: $("screen-gate"),
      dialer: $("screen-dialer"),
      call: $("screen-call"),
      ended: $("screen-ended"),
    },
    phoneScreen: $("phone-screen"),
    islandTimer: $("island-timer"),
    gateForm: $("gate-form"),
    passcode: $("passcode"),
    gateError: $("gate-error"),
    dialError: $("dial-error"),
    callButton: $("call-button"),
    liveDisc: $("live-disc"),
    wave: $("wave"),
    callState: $("call-state"),
    callTimer: $("call-timer"),
    speakingLabel: $("speaking-label"),
    captions: $("captions"),
    muteButton: $("mute-button"),
    muteLabel: $("mute-label"),
    endButton: $("end-button"),
    limitNote: $("limit-note"),
    ringNote: $("ring-note"),
    openerList: $("opener-list"),
    demoPitch: $("demo-pitch"),
    demoPitchTitle: $("demo-pitch-title"),
    endedEyebrow: $("ended-eyebrow"),
    endedTitle: $("ended-title"),
    endedSub: $("ended-sub"),
    remoteAudio: $("remote-audio"),
  };

  let config = { passcodeRequired: false, maxCallSeconds: 300 };

  /* THE TOKEN IS MINTED WHEN THE CALL IS PLACED, not when the page loads. A demo link is
   * read, left open in a tab, and pressed much later; a token minted on the way in has
   * lapsed by then and /api/connect refuses it. See createToken() in call-kit.js.
   *
   * `passcode` is the code the visitor typed at the gate and the server accepted. It lives
   * here, in this closure, for as long as the tab is open and NOWHERE else - no storage, no
   * cookie, no URL - because every re-mint presents it to /api/session again, which checks
   * it again. That is stronger than what it replaces: changing DEMO_PASSCODE now stops an
   * open tab calling within one token's life, instead of it having been asked for once, an
   * hour ago, and never since. */
  let passcode = "";
  const sessionToken = createToken(() => postJson("/api/session", { passcode }));

  // Live call. Held at module scope so nothing here is garbage collected mid-call —
  // in particular remoteSource, which is what keeps the WebAudio graph alive.
  let client = null;
  let audioCtx = null;
  let remoteSource = null;
  let remoteAnalyser = null;
  let micSource = null;
  let micAnalyser = null;
  let timerTimer = null;
  let stopWave = null;
  let startedAt = 0;
  let ended = false;
  let isConnected = false;
  let returnTimer = null;
  let callT0 = 0;

  const ringer = createRinger();
  // The waveform reads the analysers every frame rather than being handed them, because
  // they are attached as tracks arrive - which is after the call screen is up.
  const waveSources = () => [remoteAnalyser, micAnalyser];

  /* ── screens ────────────────────────────────────────────────────── */

  function show(name) {
    for (const [key, node] of Object.entries(el.screens)) node.hidden = key !== name;
  }

  function fail(node, message) {
    node.textContent = message;
    node.hidden = false;
  }

  /* ── captions ───────────────────────────────────────────────────── */

  let callerLive = null;
  let agentLive = null;
  let agentBuffer = "";
  const caption = createCaptions(el.captions);


  /* ── audio ──────────────────────────────────────────────────────────
   * The rule, and why it is written out on both pages rather than hidden in a helper:
   *
   * The bot's voice MUST be played through the <audio> element. That is the path the
   * browser's AEC uses as its reference signal when it subtracts what the speakers
   * are playing from what the microphone hears. Route it through
   * audioCtx.destination instead and Chrome's AEC flatlines: the agent hears itself,
   * transcribes itself, and interrupts itself.
   *
   * `tap()` (call-kit.js) takes the analysers off both tracks and connects them to
   * nothing, so the waveform costs no second playback path and no second microphone.
   *
   * The transport does not play anything itself (there is no srcObject or .play() in
   * @pipecat-ai/daily-transport), so this is the only audio output in the app. */

  function attachBotAudio(track) {
    const stream = new MediaStream([track]);
    el.remoteAudio.srcObject = stream;
    el.remoteAudio.play().catch(() => {});

    // Chrome only routes a WebRTC stream into WebAudio when it is also attached to a
    // media element, so both the element above and this tap are required.
    const tapped = tap(audioCtx, track);
    if (tapped) ({ source: remoteSource, analyser: remoteAnalyser } = tapped);
  }

  function attachMicAnalyser(track) {
    if (micAnalyser) return;
    const tapped = tap(audioCtx, track);
    if (tapped) ({ source: micSource, analyser: micAnalyser } = tapped);
  }

  /* ── the call ───────────────────────────────────────────────────── */

  function setCallState(text) {
    el.callState.textContent = text;
  }

  /* The cold-start script. A Pipecat Cloud container that is not already warm takes
   * ten to twenty seconds to come up, and a ringing screen that says nothing for that
   * long reads as a hang - people hang up before the agent ever picks up. One line at
   * four seconds was not enough: it is the stretch after it that feels long. So the
   * note is a short script, in the order the agent actually boots (settings, voice,
   * greeting, then the turn-detection models, then it joins the room), and it only turns
   * to "try again" once the wait is genuinely abnormal.
   *
   * Timed from the tap, not from /api/connect returning: the cold start is usually inside
   * that request, and a note armed after it would miss the very wait it is there for.
   * A warm call connects before the first line fires, so it never shows any of this. */
  const RING_NOTES = [
    [3, "Waking the line — the first call takes about ten seconds."],
    [8, "Starting the agent…"],
    [13, "Loading its voice…"],
    [18, "Nearly there — when it picks up, just talk."],
    [28, "Taking longer than usual. Hang on a moment."],
    [45, "Still not through. Hang up and try again."],
  ];

  const ringNotes = createNotes(el.ringNote, RING_NOTES);

  /** The call screen as it looks the instant before a call connects. */
  function resetCallScreen() {
    el.phoneScreen.classList.remove("is-live");
    el.liveDisc.classList.remove("is-connected", "is-agent", "is-caller");
    el.liveDisc.classList.add("is-ringing");
    el.callState.hidden = false;
    setCallState("calling…");
    el.speakingLabel.textContent = "ringing";
    el.callTimer.hidden = true;
    el.callTimer.textContent = "00:00";
    el.islandTimer.textContent = "00:00";
    el.limitNote.hidden = true;
    ringNotes.clear();
    el.muteButton.setAttribute("aria-pressed", "false");
    el.muteLabel.textContent = "mute";
  }

  /* A handset does not offer to redial. It shows the call ended, then puts you back
   * where you were - so the ended screen clears itself rather than growing a button.
   * The write-up beside the phone stays where it is. */
  function returnToDialer() {
    clearTimeout(returnTimer);
    returnTimer = null;
    resetCallScreen();
    show("dialer");
  }

  function buildClient() {
    return new PipecatClient({
      transport: new DailyTransport({
        // Deliberately NOT bufferLocalAudioUntilBotReady. It sounds right - hold what
        // the caller says before the bot is ready - but the agent mutes the caller
        // until the greeting completes (MuteUntilFirstBotCompleteUserMuteStrategy in
        // services.build_user_mute_strategies), so the buffered audio is discarded
        // server-side anyway. Enabling it only puts a recorder and an AudioWorklet on
        // the microphone path, and its teardown double-frees on a failed join.
        //
        // Spread into Daily.createCallObject() and applied by startCamera(). Daily
        // replace their own constraints wholesale rather than merging, so these three
        // are stated explicitly and then read back in reportAudioConstraints().
        inputSettings: {
          audio: {
            settings: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          },
        },
      }),
      enableMic: true,
      enableCam: false,
      callbacks: {
        onBotReady: () => connected(),

        onUserStartedSpeaking: () => {
          el.liveDisc.classList.add("is-caller");
          el.liveDisc.classList.remove("is-agent");
          el.speakingLabel.textContent = "Listening";
        },
        onUserStoppedSpeaking: () => {
          el.liveDisc.classList.remove("is-caller");
          el.speakingLabel.textContent = "On the call";
        },

        onBotStartedSpeaking: () => {
          // bot-ready is the correct signal, but relying on one signal for an audible
          // tone is how this broke before. If the agent is talking, we are connected.
          connected();
          el.liveDisc.classList.add("is-agent");
          el.liveDisc.classList.remove("is-caller");
          el.speakingLabel.textContent = "Speaking";
          agentBuffer = "";
          agentLive = caption("Auto Fix Auto Electricians", "caption-agent");
        },
        onBotStoppedSpeaking: () => {
          el.liveDisc.classList.remove("is-agent");
          el.speakingLabel.textContent = "On the call";
          agentLive = null;
        },

        onBotTtsText: (data) => {
          if (agentLive && data && data.text) {
            agentBuffer += (agentBuffer ? " " : "") + data.text;
            agentLive.textContent = agentBuffer;
            el.captions.scrollTop = el.captions.scrollHeight;
          }
        },
        onBotOutput: (data) => {
          // Fallback: only if nothing streamed for this turn.
          if (agentLive && !agentBuffer && data && data.text) {
            agentLive.textContent = data.text;
          }
        },

        onUserTranscript: (data) => {
          if (!data || !data.text) return;
          if (!callerLive) callerLive = caption("You", "caption-caller");
          callerLive.textContent = data.text;
          el.captions.scrollTop = el.captions.scrollHeight;
          if (data.final) callerLive = null;
        },

        onTrackStarted: (track, participant) => {
          if (track.kind !== "audio") return;
          if (participant && participant.local) attachMicAnalyser(track);
          else attachBotAudio(track);
        },

        onDeviceError: (error) => {
          console.warn("device error", error);
          hangUp("This needs your microphone. Allow access and try again.");
        },

        // The bot hangs up by leaving the room. Ending on that, not on our own
        // connection dropping, is what stops the handset showing "on the call" to an
        // empty room while Pipecat Cloud tears it down.
        onBotDisconnected: () => hangUp(null),

        onDisconnected: () => hangUp(null),

        onError: (message) => console.warn("agent error", message),
        onMessageError: (message) => console.warn("agent message error", message),
      },
    });
  }

  /** What a mint that failed means. /api/session refuses nothing but the passcode. */
  function mintFailed({ status }) {
    if (status === 401 && config.passcodeRequired) {
      // The code was right when the visitor typed it; DEMO_PASSCODE has changed since.
      passcode = "";
      show("gate");
      fail(el.gateError, "That passcode is not right any more. Check the code you were sent.");
      el.passcode.value = "";
      el.passcode.focus();
      return;
    }
    // Unconfigured, unreachable, or a 500. Recoverable, and the button is right there:
    // pressing it again mints again, which is the whole of what this buys.
    show("dialer");
    fail(el.dialError, "Could not reach the demo server. Try again.");
  }

  /** Stop what the call screen started WITHOUT the ended screen: the caller is about to put
   *  a different one up, and hangUp() would schedule a return to the dialer over the top. */
  function abandonCall() {
    ended = true;
    clearTimeout(returnTimer);
    returnTimer = null;
    teardown();
    resetCallScreen();
  }

  async function placeCall() {
    isConnected = false;
    el.dialError.hidden = true;
    // Nothing should be callable before the gate is cleared, or the call fails at
    // the offer and the visitor gets an error instead of an explanation.
    if (config.passcodeRequired && !passcode) {
      show("gate");
      el.passcode.focus();
      return;
    }

    // BEFORE THE FIRST AWAIT, while this tap still counts (rule ONE in call-kit.js). The
    // play() has no source yet; what it buys is the element's leave to play the bot's
    // track when that arrives, seconds from now. A context left open by a failed mint is
    // reused rather than leaked.
    if (!audioCtx) audioCtx = openAudioContext();
    el.remoteAudio.play().catch(() => {});

    // MINTED HERE, a moment before it is spent. A page that has been open since this
    // morning is the normal case for a demo link, not the exception.
    const minted = await sessionToken.get();
    if (!minted.ok) return mintFailed(minted);

    ended = false;

    clearTimeout(returnTimer);
    returnTimer = null;
    show("call");
    resetCallScreen();
    callT0 = performance.now();
    ringNotes.schedule(callT0);
    el.captions.replaceChildren();
    startedAt = 0;

    // Start the agent. Pipecat Cloud creates the Daily room, puts the agent in it and
    // returns the room URL with a meeting token for us.
    let session;
    try {
      let res = await postJson("/api/connect", { token: minted.token });
      if (res.status === 401) {
        // The server refused a token this page thought was live: a browser clock further
        // behind than the margin, or a secret rotated under us. One re-mint and one retry,
        // because nobody should pay a reload for either. A second 401 is a real refusal.
        sessionToken.clear();
        const again = await sessionToken.get();
        if (!again.ok) {
          abandonCall();
          return mintFailed(again);
        }
        res = await postJson("/api/connect", { token: again.token });
      }
      if (res.status === 503) return hangUp("The demo line is busy. Try again shortly.");
      if (res.status === 401) return hangUp("Couldn't start the call. Try again.");
      if (!res.ok) return hangUp("Couldn't get through to Auto Fix.");
      session = await res.json();
    } catch (err) {
      console.error(err);
      return hangUp("Couldn't get through to Auto Fix.");
    }

    ringer.start(audioCtx, "ringback");
    setCallState("ringing…");

    client = buildClient();
    try {
      // DailyTransport normalises dailyRoom -> url and dailyToken -> token itself, so
      // the Pipecat Cloud response goes in unreshaped.
      await client.connect(session);
      reportAudioConstraints(client);
    } catch (err) {
      console.error(err);
      hangUp("Couldn't get through to Auto Fix.");
    }
  }

  function connected() {
    if (isConnected) return;
    isConnected = true;
    ringer.stop();
    ringNotes.clear();
    el.liveDisc.classList.remove("is-ringing");
    el.liveDisc.classList.add("is-connected");
    // Connected, so the handset drops the "calling" line and shows the clock -
    // in the status line and in the island, which widens to carry it.
    el.callState.hidden = true;
    el.callTimer.hidden = false;
    el.phoneScreen.classList.add("is-live");
    el.speakingLabel.textContent = "on the call";
    startedAt = Date.now();
    stopWave = startWave(el.wave, accentColour("#ff6b6b"), waveSources);

    timerTimer = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      const reading = clock(secs);
      el.callTimer.textContent = reading;
      el.islandTimer.textContent = reading;
      if (config.maxCallSeconds > 0) {
        const left = config.maxCallSeconds - secs;
        if (left <= 60 && left > 0) {
          el.limitNote.hidden = false;
          el.limitNote.textContent = `This demo call ends in ${left}s.`;
        }
      }
    }, 500);
  }

  function teardown() {
    clearInterval(timerTimer);
    timerTimer = null;
    ringer.stop();
    ringNotes.clear();
    if (stopWave) stopWave();
    stopWave = null;
    if (client) {
      // Fire and forget: the page may be unloading, and there is nothing to wait for.
      client.disconnect().catch(() => {});
      client = null;
    }
    if (audioCtx) audioCtx.close().catch(() => {});
    el.remoteAudio.srcObject = null;
    audioCtx = remoteSource = remoteAnalyser = micSource = micAnalyser = null;
  }

  function hangUp(reason) {
    if (ended) return;
    ended = true;
    const secs = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0;
    teardown();

    el.phoneScreen.classList.remove("is-live");
    el.endedEyebrow.textContent = secs ? `Call Ended · ${clock(secs)}` : "Call Ended";
    el.endedTitle.textContent = "Auto Fix Auto Electricians";
    el.endedSub.textContent = reason || "";

    show("ended");
    // Long enough to read, short enough that nobody wonders what to do next.
    returnTimer = setTimeout(returnToDialer, reason ? 4000 : 2400);
  }

  /* ── wiring ─────────────────────────────────────────────────────── */

  el.callButton.addEventListener("click", placeCall);

  el.endButton.addEventListener("click", () => hangUp(null));

  el.muteButton.addEventListener("click", () => {
    if (!client) return;
    // The transport owns the track, so this goes through the client rather than
    // flipping track.enabled behind its back. aria-pressed means muted; the label is
    // the next action, not the current state.
    const wasEnabled = client.isMicEnabled;
    client.enableMic(!wasEnabled);
    el.muteButton.setAttribute("aria-pressed", String(wasEnabled));
    el.muteLabel.textContent = wasEnabled ? "unmute" : "mute";
  });

  el.gateForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    el.gateError.hidden = true;
    // The gate IS a mint: /api/session is what checks the passcode, and what is kept
    // afterwards is the code as well as the token, so a call placed three hours from now
    // can mint again without asking the visitor for a number they no longer have in front
    // of them.
    passcode = el.passcode.value;
    const minted = await sessionToken.get();
    if (minted.ok) return show("dialer");

    passcode = "";
    fail(el.gateError, minted.status === 401
      ? "That passcode is not right."
      : "Could not reach the demo server. Try again.");
    el.passcode.select();
  });

  window.addEventListener("beforeunload", teardown);

  /* ── start ──────────────────────────────────────────────────────── */

  (async () => {
    // The two pieces of business copy on the page, both from readout.js.
    renderOpeners(el.openerList, OPENERS);
    renderPitch(el.demoPitch, PITCH, el.demoPitchTitle);
    try {
      config = await (await fetch("/api/config")).json();
    } catch (_) {
      fail(el.dialError, "Could not reach the demo server.");
    }
    if (config.passcodeRequired) {
      show("gate");
      el.passcode.focus();
    } else {
      // No gate, so there is nothing to ask for and nothing to mint yet: the token is
      // minted when the button is pressed.
      show("dialer");
    }
  })();
})();
